import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { urls } from '../../../config';
import { useSubmitAndCloseDropdown } from '../../hooks/useSubmitAndCloseDropdown';
import { bookingModeSelector } from '../../redux/booking/booking/selectors';
import {
  selectedNewspaperSelector,
  setAvailablePromoDates,
  setCodeWordsValidated,
  setNewspaper,
} from '../../redux/codeWordsSlice';
import { getPromotionSelector } from '../../redux/init/selectors';
import { resetArrival, selectChannel } from '../../redux/search/form/actions';
import { searchFormChannelIdSelector } from '../../redux/search/form/selectors';
import { email } from '../../utils/validation';
import { TextInput } from '../FormElements';
import { NewspaperSelect } from './NewsPaperSelect';

export const EnterCodesModal = ({ isHomePage, className = '' }) => {
  const {
    selectedInitPromoValues,
    bookingMode,
    codeWordsIterator,
    codeWords,
    onChangeCodeWords,
    codeWordsInvalid,
    showNewspaperField,
    showEmailField,
    handleNewsPaperSelect,
    handleNewsPaperToggle,
    validateSubmit,
    emailInvalid,
    emailAddress,
    showNewspaperSelect,
    selectedNewspaper,
    showNewspaperError,
    onChangeEmailAddress,
  } = usePromoForm();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalToggle = () => setIsModalOpen((show) => !show);

  const buttonColor = isHomePage ? 'btn-primary-yellow' : 'btn-primary';

  return (
    <>
      {selectedInitPromoValues?.open && bookingMode && (
        <div
          className={`${isHomePage ? 'bg-primary' : 'bg-dark'} d-flex justify-content-center w-100 py-4 ${className}`}
        >
          <Button
            variant="primary"
            onClick={handleModalToggle}
            className={`btn-lg ${buttonColor}`}
            style={{ fontSize: isHomePage ? 'inherit' : '20px' }}
          >
            Search For Your Holiday
          </Button>
        </div>
      )}

      <Modal show={isModalOpen} onHide={handleModalToggle} centered>
        <Modal.Header closeButton>
          <Modal.Title>Find your holiday</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              {codeWordsIterator.length > 0 && (
                <>
                  {codeWordsIterator.map((i) => (
                    <TextInput
                      key={`code_word_${i}`}
                      groupClassName="col-6"
                      name={`Code Word ${i + 1}`}
                      label={`Password ${i + 1}`}
                      value={codeWords[i]}
                      onChange={(e) => onChangeCodeWords(e, i)}
                    />
                  ))}
                  {codeWordsInvalid && (
                    <Form.Control.Feedback
                      type="invalid"
                      className="col-12"
                      style={{ display: 'block', marginBottom: '16px' }}
                    >
                      Please input correct code words
                    </Form.Control.Feedback>
                  )}
                </>
              )}
              {showNewspaperField && (
                <>
                  <Col md={12} lg={12}>
                    <NewspaperSelect
                      id="searchbox-newspaper"
                      textStyle="black"
                      label="Newspaper"
                      onChange={handleNewsPaperSelect}
                      onToggle={handleNewsPaperToggle}
                      show={showNewspaperSelect}
                      selectedValue={selectedNewspaper}
                    />
                  </Col>
                  {showNewspaperError && (
                    <Form.Control.Feedback
                      type="invalid"
                      className="col-12"
                      style={{ display: 'block', marginBottom: '16px' }}
                    >
                      Please input a newspaper
                    </Form.Control.Feedback>
                  )}
                </>
              )}

              {showEmailField && (
                <>
                  <p className="col-12">
                    Enter your email address to receive offer updates and exclusive deals from Breakfree Holidays
                  </p>
                  <TextInput
                    groupClassName="col-12"
                    name="Code Words"
                    NewspaperSelect
                    label="Email Address"
                    value={emailAddress}
                    invalid={emailInvalid}
                    validationMessage="Please input valid email"
                    onChange={(e) => onChangeEmailAddress(e)}
                  />
                </>
              )}
              <Col>
                <Button onClick={validateSubmit} block>
                  Continue
                </Button>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
};

export const usePromoForm = () => {
  const dispatch = useDispatch();
  const selectedInitPromoValues = useSelector(getPromotionSelector);
  const showEmailField = selectedInitPromoValues?.emailcapture === 'Y';
  const showNewspaperField = selectedInitPromoValues?.papercapture === 'Y';
  const numberOfPasswords = parseInt(selectedInitPromoValues?.keywords || 0);

  const bookingMode = useSelector(bookingModeSelector) || true;

  const selectedChannelId = useSelector(searchFormChannelIdSelector);
  const selectedNewspaper = useSelector(selectedNewspaperSelector);

  const [codeWords, setCodeWords] = useState([]);
  const [codeWordsInvalid, setCodeWordsInvalid] = useState(null);

  const [showNewspaperError, setShowNewspaperError] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(null);

  const codeWordsIterator = useMemo(() => Array.from({ length: numberOfPasswords }, (_, i) => i), [numberOfPasswords]);

  useEffect(() => {
    if (window.location.pathname === '/') {
      selectChannel(dispatch, 'ukpromo');
    }

    const initializeArray = (length) => Array(length).fill('');
    setCodeWords(initializeArray(numberOfPasswords));
  }, [dispatch, numberOfPasswords, selectedInitPromoValues?.papercodes]);

  const onChangeCodeWords = (e, i) => {
    const codeWordsClone = [...codeWords];

    codeWordsClone.splice(i, 1, e.target.value);
    setCodeWords(codeWordsClone);
    setCodeWordsInvalid(null);
  };

  const calculateShouldResetArrival = (previousChannel) => {
    if (['eupromo', 'ukpromo'].includes(previousChannel)) {
      resetArrival(dispatch);
    }
  };

  const onChangeEmailAddress = (e) => {
    const emailValue = e.target.value;
    setEmailAddress(emailValue);
    setEmailInvalid(!email(emailValue));
  };

  const { submitAndCloseDropdown } = useSubmitAndCloseDropdown();

  const [showNewspaperSelect, setShowNewspaperSelect] = useState(false);
  const handleNewsPaperToggle = () => setShowNewspaperSelect(!showNewspaperSelect);
  const handleNewsPaperSelect = (event) => {
    const value = event.target.value;
    submitAndCloseDropdown(value, setNewspaper, setShowNewspaperSelect, true);
    if (value && showNewspaperError) {
      setShowNewspaperError(false);
    }
    calculateShouldResetArrival(selectedChannelId);
  };

  const validateForm = () => {
    const errors = {
      email: showEmailField && !email(emailAddress),
      newspaper: showNewspaperField && !selectedNewspaper,
    };

    setEmailInvalid(errors.email);
    setShowNewspaperError(errors.newspaper);

    return !errors.email && !errors.newspaper;
  };

  const validateSubmit = () => {
    if (validateForm()) {
      dataFetching();
    }
  };

  const dataFetching = () => {
    const formBody = [];

    if (showNewspaperField) {
      formBody.push(`newspaper=${encodeURIComponent(selectedNewspaper)}`);
    }

    if (showEmailField) {
      formBody.push(`email=${encodeURIComponent(emailAddress)}`);
    }

    codeWords.forEach((word, index) => {
      formBody.push(`code${index + 1}=${encodeURIComponent(word)}`);
    });

    const makeRequest = () => {
      fetch(`${urls.validatecodewords}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: formBody.join('&'),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success === 1) {
            dispatch(setCodeWordsValidated());
            fetchPromoData();
          } else {
            setCodeWordsInvalid(true);
          }
        })
        .catch(console.error);
    };

    const fetchPromoData = () => {
      fetch(`${urls.getpromodata}`)
        .then((response) => response.json())
        .then((data) => {
          dispatch(setAvailablePromoDates(data?.data?.DATES));
        });
    };

    // Past a certain date codeWords don't need to be defined
    if (numberOfPasswords > 0 && codeWords.length > 0) {
      makeRequest(formBody);
    } else {
      dispatch(setCodeWordsValidated());
      fetchPromoData();
    }
  };

  return {
    selectedInitPromoValues,
    bookingMode,
    codeWordsIterator,
    codeWords,
    onChangeCodeWords,
    codeWordsInvalid,
    showNewspaperField,
    handleNewsPaperSelect,
    handleNewsPaperToggle,
    validateSubmit,
    emailInvalid,
    emailAddress,
    selectedNewspaper,
    showNewspaperError,
    onChangeEmailAddress,
    showNewspaperSelect,
    showEmailField,
  };
};
